'use client';

import { BrainCircuit, Clock, Link2Off, ShieldOff, Timer } from 'lucide-react';
import { ReactNode } from 'react';
import Image from 'next/image';

const problems = [
  {
    title: 'Ricerche infinite',
    description:
      'Passi ore tra codici, sentenze e circolari senza avere certezza di coprire tutto.',
    icon: <BrainCircuit />,
  },
  {
    title: 'Norme superate',
    description: 'Un aggiornamento mancato può rendere il tuo parere obsoleto.',
    icon: <Clock />,
  },
  {
    title: 'Pareri incompleti',
    description:
      'Collegamenti mancanti tra norme, giurisprudenza e prassi creano vuoti pericolosi.',
    icon: <Link2Off />,
  },
  {
    title: 'Fonti non verificabili',
    description: 'Senza riferimenti ufficiali, la tua analisi perde forza e credibilità.',
    icon: <ShieldOff />,
  },
  {
    title: 'Pressione e tempi stretti',
    description: 'Clienti e colleghi vogliono risposte rapide e solide, sempre.',
    icon: <Timer />,
  },
];

const ProblemSection = () => {
  return (
    <section id="problems" className="py-16 px-4 md:px-8 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-primary font-funnel mb-6">
          Ogni giorno, professionisti come te affrontano{' '}
          <span className="text-accent">grandi sfide</span>
        </h2>
        <p className="text-lg text-neutral-700 mb-12">
          Anche il più esperto può trovarsi bloccato quando la normativa cambia di continuo, le
          fonti sono incomplete o le interpretazioni poco chiare.
        </p>
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <ProblemCard
              title={problems[0].title}
              description={problems[0].description}
              icon={problems[0].icon}
            />
            <ProblemCard
              title={problems[1].title}
              description={problems[1].description}
              icon={problems[1].icon}
            />
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <ProblemCard
              title={problems[2].title}
              description={problems[2].description}
              icon={problems[2].icon}
            />
            <Image
              src="/images/problems/problems.webp"
              alt="Problems image"
              width={160}
              height={160}
              className="rounded-full object-contain mx-0 my-0 self-center hidden md:block"
            />
            <ProblemCard
              title={problems[3].title}
              description={problems[3].description}
              icon={problems[3].icon}
            />
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <ProblemCard
              title={problems[4].title}
              description={problems[4].description}
              icon={problems[4].icon}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

interface ProblemCardProps {
  title: string;
  description: string;
  icon: ReactNode;
}

const ProblemCard = ({ title, description, icon }: ProblemCardProps) => {
  return (
    // Layout principale modificato con Flexbox
    <div className="w-full md:w-xs flex items-start space-x-6 rounded-xl bg-white p-6 shadow-md hover:shadow-lg transition duration-200 border border-neutral-200">
      {/* Container per l'icona */}
      <div className="flex-shrink-0 bg-accent-100 text-accent rounded-full p-4">{icon}</div>

      {/* Container per il testo */}
      <div className="text-left">
        <h3 className="text-lg font-semibold text-gray-800 mb-1">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default ProblemSection;
